import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { ProtectedRoute } from '../components/layout/ProtectedRoute.jsx';
import { FeatureRoute } from '../components/layout/FeatureRoute.jsx';
import { AppShell, MainLayout } from '../components/layout/AppShell.jsx';
import { MenuDrawer } from '../components/layout/MenuDrawer.jsx';
import { Skeleton } from '../components/ui/Skeleton.jsx';
import { YogiBubble } from '../features/yogi/components/YogiBubble.jsx';

const IntroPage = lazy(() => import('../features/auth/pages/IntroPage.jsx').then((m) => ({ default: m.IntroPage })));
const InstallPage = lazy(() => import('../features/install/pages/InstallPage.jsx').then((m) => ({ default: m.InstallPage })));
const WelcomePage = lazy(() => import('../features/auth/pages/WelcomePage.jsx').then((m) => ({ default: m.WelcomePage })));
const LoginPage = lazy(() => import('../features/auth/pages/LoginPage.jsx').then((m) => ({ default: m.LoginPage })));
const FeedPage = lazy(() => import('../features/feed/pages/FeedPage.jsx').then((m) => ({ default: m.FeedPage })));
const ChatListPage = lazy(() => import('../features/chat/pages/ChatListPage.jsx').then((m) => ({ default: m.ChatListPage })));
const ChatScreenPage = lazy(() => import('../features/chat/pages/ChatScreenPage.jsx').then((m) => ({ default: m.ChatScreenPage })));
const NewChatPage = lazy(() => import('../features/chat/pages/ChatScreenPage.jsx').then((m) => ({ default: m.NewChatPage })));
const FriendsPage = lazy(() => import('../features/friends/pages/FriendsPage.jsx').then((m) => ({ default: m.FriendsPage })));
const NotificationsPage = lazy(() => import('../features/notifications/pages/NotificationsPage.jsx').then((m) => ({ default: m.NotificationsPage })));
const ProfilePage = lazy(() => import('../features/profile/pages/ProfilePage.jsx').then((m) => ({ default: m.ProfilePage })));
const UserProfilePage = lazy(() => import('../features/profile/pages/ProfilePage.jsx').then((m) => ({ default: m.UserProfilePage })));
const SettingsPage = lazy(() => import('../features/settings/pages/SettingsPage.jsx').then((m) => ({ default: m.SettingsPage })));
const AdminToolsPage = lazy(() => import('../features/settings/pages/AdminToolsPage.jsx').then((m) => ({ default: m.AdminToolsPage })));
const MyPostsPage = lazy(() => import('../features/posts/pages/MyPostsPage.jsx').then((m) => ({ default: m.MyPostsPage })));
const CoursesPage = lazy(() => import('../features/courses/pages/CoursesPage.jsx').then((m) => ({ default: m.CoursesPage })));
const SchedulePage = lazy(() => import('../features/schedule/pages/SchedulePage.jsx').then((m) => ({ default: m.SchedulePage })));
const ManualsPage = lazy(() => import('../features/manuals/pages/ManualsPage.jsx').then((m) => ({ default: m.ManualsPage })));
const VideosPage = lazy(() => import('../features/videos/pages/VideosPage.jsx').then((m) => ({ default: m.VideosPage })));
const UserVideosPage = lazy(() => import('../features/videos/pages/VideosPage.jsx').then((m) => ({ default: m.UserVideosPage })));
const MeetingsPage = lazy(() => import('../features/meetings/pages/MeetingsPage.jsx').then((m) => ({ default: m.MeetingsPage })));
const JitsiPage = lazy(() => import('../features/meetings/pages/MeetingsPage.jsx').then((m) => ({ default: m.JitsiPage })));
const MeetingFinishedPage = lazy(() => import('../features/meetings/pages/MeetingsPage.jsx').then((m) => ({ default: m.MeetingFinishedPage })));
const NotFoundPage = lazy(() => import('../features/auth/pages/NotFoundPage.jsx').then((m) => ({ default: m.NotFoundPage })));

function PageLoader() {
  return (
    <div className="flex min-h-screen flex-col gap-4 p-4">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/intro" element={<IntroPage />} />
          <Route path="/install" element={<InstallPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route element={<MainLayout />}>
              <Route index element={<FeedPage />} />
              <Route
                path="chats"
                element={
                  <FeatureRoute feature="chat">
                    <ChatListPage />
                  </FeatureRoute>
                }
              />
              <Route
                path="friends"
                element={
                  <FeatureRoute feature="friends">
                    <FriendsPage />
                  </FeatureRoute>
                }
              />
              <Route
                path="notifications"
                element={
                  <FeatureRoute feature="notifications">
                    <NotificationsPage />
                  </FeatureRoute>
                }
              />
            </Route>

            <Route
              path="chat/:chatId"
              element={
                <FeatureRoute feature="chat">
                  <ChatScreenPage />
                </FeatureRoute>
              }
            />
            <Route
              path="chat/new"
              element={
                <FeatureRoute feature="chat">
                  <NewChatPage />
                </FeatureRoute>
              }
            />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="user/:userId" element={<UserProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="admin" element={<AdminToolsPage />} />
            <Route
              path="my-posts"
              element={
                <FeatureRoute feature="posting">
                  <MyPostsPage />
                </FeatureRoute>
              }
            />
            <Route
              path="courses"
              element={
                <FeatureRoute feature="courses">
                  <CoursesPage />
                </FeatureRoute>
              }
            />
            <Route
              path="schedule"
              element={
                <FeatureRoute feature="schedule">
                  <SchedulePage />
                </FeatureRoute>
              }
            />
            <Route
              path="manuals"
              element={
                <FeatureRoute feature="manuals">
                  <ManualsPage />
                </FeatureRoute>
              }
            />
            <Route
              path="videos"
              element={
                <FeatureRoute feature="videos">
                  <VideosPage />
                </FeatureRoute>
              }
            />
            <Route
              path="videos/:userId"
              element={
                <FeatureRoute feature="videos">
                  <UserVideosPage />
                </FeatureRoute>
              }
            />
            <Route
              path="meetings"
              element={
                <FeatureRoute feature="meetings">
                  <MeetingsPage />
                </FeatureRoute>
              }
            />
            <Route
              path="video/:roomName/:displayName"
              element={
                <FeatureRoute feature="videoConference">
                  <JitsiPage />
                </FeatureRoute>
              }
            />
            <Route
              path="meeting-finished"
              element={
                <FeatureRoute feature="videoConference">
                  <MeetingFinishedPage />
                </FeatureRoute>
              }
            />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <MenuDrawer />
      <YogiBubble />
    </BrowserRouter>
  );
}
